import React, { useState, useEffect } from 'react';
import { toast } from '../../toast/CustomToast'
import api from '../../../utils/api';

const parameterTypeLabels = {
    string: 'Texto',
    integer: 'Número Inteiro',
    float: 'Número Decimal',
    boolean: 'Sim/Não',
    date: 'Data',
    datetime: 'Data e Hora',
    json: 'JSON'
};

const ParametersModal = ({
    isOpen,
    onClose,
    integration,
    companyId
}) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [parameterValues, setParameterValues] = useState([]);

    useEffect(() => {
        if (isOpen && integration) {
            loadParameters();
        }
    }, [isOpen, integration]);

    const loadParameters = async () => {
        setLoading(true);
        try {
            const response = await api.get(
                `/company/integrations/${integration.id}/parameter-values`
            );
            setParameterValues(response.data?.items || []);
        } catch (error) {
            console.error('Erro ao carregar parâmetros:', error);
            toast.error('Erro ao carregar parâmetros');
        } finally {
            setLoading(false);
        }
    };

    const updateParameterValue = (index, value) => {
        setParameterValues(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], value };
            return updated;
        });
    };

    const validateRequiredFields = () => {
        const missingFields = parameterValues.filter(param =>
            param.required && (!param.value || param.value.trim() === '')
        );

        if (missingFields.length > 0) {
            toast.error('Por favor, preencha todos os campos obrigatórios');
            return false;
        }

        return true;
    };

    const saveParameters = async () => {
        if (!validateRequiredFields()) {
            return;
        }

        setSaving(true);
        try {
            const payload = {
                values: parameterValues.map(param => ({
                    parameter_id: param.parameter_id,
                    value: param.value || ''
                }))
            };

            await api.post(
                `/company/integrations/${integration.id}/parameter-values`,
                payload
            );

            toast.success('Configurações salvas com sucesso!');
            onClose();
        } catch (error) {
            console.error('Erro ao salvar configurações:', error);
            toast.error('Erro ao salvar configurações');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen || !integration) return null;

    return (
        <>
            {/* Overlay */}
            <div className="fixed inset-0 bg-black/50 z-30" onClick={onClose}></div>

            {/* Modal */}
            <div className="fixed right-0 top-0 h-screen w-full max-w-lg bg-white dark:bg-gray-800 shadow-xl z-40 flex flex-col">
                {/* Header fixo */}
                <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="material-icons text-amber-500 dark:text-amber-400" style={{fontSize: '28px'}}>settings</span>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Configurações</h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{integration.name}</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                        >
                            <span className="material-icons">close</span>
                        </button>
                    </div>
                </div>

                {/* Conteúdo scrollável */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                        </div>
                    ) : parameterValues.length === 0 ? (
                        <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                            <span className="material-icons text-gray-400 dark:text-gray-500 mb-3" style={{fontSize: '48px'}}>tune</span>
                            <p className="text-gray-500 dark:text-gray-400">Nenhuma configuração disponível</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                Preencha as configurações necessárias para esta integração
                            </p>

                            {parameterValues.map((param, index) => (
                                <div key={param.parameter_id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                                    {/* Header do parâmetro */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <label className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {param.title}
                                                </label>
                                                {param.required && (
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                                                        Obrigatório
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{param.code}</span>
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                                    {parameterTypeLabels[param.param_type] || param.param_type}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Campo de entrada baseado no tipo */}
                                    {param.param_type === 'boolean' ? (
                                        <div className="flex items-center">
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={param.value === 'true' || param.value === true}
                                                    onChange={(e) => updateParameterValue(index, e.target.checked ? 'true' : 'false')}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-500 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-amber-500"></div>
                                                <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    {param.value === 'true' || param.value === true ? 'Sim' : 'Não'}
                                                </span>
                                            </label>
                                        </div>
                                    ) : param.param_type === 'integer' ? (
                                        <input
                                            type="number"
                                            value={param.value || ''}
                                            onChange={(e) => updateParameterValue(index, e.target.value)}
                                            placeholder="Digite um número inteiro"
                                            step="1"
                                            className={`w-full px-3 py-2 text-sm border rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                                                param.required && (!param.value || param.value.trim() === '')
                                                    ? 'border-red-300 dark:border-red-600'
                                                    : 'border-gray-300 dark:border-gray-600'
                                            }`}
                                        />
                                    ) : param.param_type === 'float' ? (
                                        <input
                                            type="number"
                                            value={param.value || ''}
                                            onChange={(e) => updateParameterValue(index, e.target.value)}
                                            placeholder="Digite um número decimal"
                                            step="0.01"
                                            className={`w-full px-3 py-2 text-sm border rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                                                param.required && (!param.value || param.value.trim() === '')
                                                    ? 'border-red-300 dark:border-red-600'
                                                    : 'border-gray-300 dark:border-gray-600'
                                            }`}
                                        />
                                    ) : param.param_type === 'date' ? (
                                        <input
                                            type="date"
                                            value={param.value || ''}
                                            onChange={(e) => updateParameterValue(index, e.target.value)}
                                            className={`w-full px-3 py-2 text-sm border rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                                                param.required && (!param.value || param.value.trim() === '')
                                                    ? 'border-red-300 dark:border-red-600'
                                                    : 'border-gray-300 dark:border-gray-600'
                                            }`}
                                        />
                                    ) : param.param_type === 'datetime' ? (
                                        <input
                                            type="datetime-local"
                                            value={param.value || ''}
                                            onChange={(e) => updateParameterValue(index, e.target.value)}
                                            className={`w-full px-3 py-2 text-sm border rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                                                param.required && (!param.value || param.value.trim() === '')
                                                    ? 'border-red-300 dark:border-red-600'
                                                    : 'border-gray-300 dark:border-gray-600'
                                            }`}
                                        />
                                    ) : param.param_type === 'json' ? (
                                        <textarea
                                            value={param.value || ''}
                                            onChange={(e) => updateParameterValue(index, e.target.value)}
                                            placeholder='{"chave": "valor"}'
                                            rows={4}
                                            className={`w-full px-3 py-2 text-sm border rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent font-mono ${
                                                param.required && (!param.value || param.value.trim() === '')
                                                    ? 'border-red-300 dark:border-red-600'
                                                    : 'border-gray-300 dark:border-gray-600'
                                            }`}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={param.value || ''}
                                            onChange={(e) => updateParameterValue(index, e.target.value)}
                                            placeholder="Digite o valor"
                                            className={`w-full px-3 py-2 text-sm border rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                                                param.required && (!param.value || param.value.trim() === '')
                                                    ? 'border-red-300 dark:border-red-600'
                                                    : 'border-gray-300 dark:border-gray-600'
                                            }`}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer fixo */}
                {parameterValues.length > 0 && (
                    <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        <div className="flex gap-3">
                            <button
                                onClick={saveParameters}
                                disabled={saving || loading}
                                className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-400 text-white font-medium rounded-lg transition flex items-center justify-center gap-2"
                            >
                                {saving ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        Salvando...
                                    </>
                                ) : (
                                    <>
                                        <span className="material-icons" style={{fontSize: '20px'}}>save</span>
                                        Salvar
                                    </>
                                )}
                            </button>
                            <button
                                onClick={onClose}
                                disabled={saving}
                                className="flex-1 px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 font-medium rounded-lg transition flex items-center justify-center gap-2"
                            >
                                <span className="material-icons" style={{fontSize: '20px'}}>close</span>
                                Cancelar
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default ParametersModal;
